import { createThemeExtension } from "@/v2-ui/theme/theme.extension"

/**
 * Operator-tunable theming for every code editor in the backoffice (Monaco
 * instances: the layer workbench, JSON diff views, …), configured on the
 * Settings page.
 *
 * Each setting is a theme extension, so values persist and restore exactly
 * like the rest of the theme config. The stored CSS custom properties are
 * only the persistence vehicle — Monaco cannot read CSS vars, so editors
 * consume the resolved JS values through `useEditorSettings()`.
 */

// --- Color palette (maps 1:1 to a built-in Monaco theme) ---

const EDITOR_PALETTES = {
  dark: "vs-dark",
  light: "light",
  contrastDark: "hc-black",
  contrastLight: "hc-light",
} as const

export const EDITOR_PALETTE_OPTIONS = [
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
  { value: "contrastDark", label: "Contrast Dark" },
  { value: "contrastLight", label: "Contrast Light" },
] as const

const useEditorPalette = createThemeExtension({
  cssVar: "--ui-editor-palette",
  defaultValue: "dark",
  options: EDITOR_PALETTES,
})

// --- Font size ---

const EDITOR_FONT_SIZES = {
  extraSmall: 11,
  small: 12,
  default: 13,
  large: 14,
  extraLarge: 16,
} as const

export const EDITOR_FONT_SIZE_OPTIONS = [
  { value: "extraSmall", label: "Extra Small" },
  { value: "small", label: "Small" },
  { value: "default", label: "Default" },
  { value: "large", label: "Large" },
  { value: "extraLarge", label: "Extra Large" },
] as const

const useEditorFontSize = createThemeExtension({
  cssVar: "--ui-editor-font-size",
  defaultValue: "default",
  options: Object.fromEntries(
    Object.entries(EDITOR_FONT_SIZES).map(([key, px]) => [key, `${px}px`]),
  ) as Record<keyof typeof EDITOR_FONT_SIZES, string>,
})

// --- Line height (multiplier of the font size) ---

const EDITOR_LINE_HEIGHTS = {
  compact: 1.3,
  default: 1.5,
  relaxed: 1.8,
} as const

export const EDITOR_LINE_HEIGHT_OPTIONS = [
  { value: "compact", label: "Compact" },
  { value: "default", label: "Default" },
  { value: "relaxed", label: "Relaxed" },
] as const

const useEditorLineHeight = createThemeExtension({
  cssVar: "--ui-editor-line-height",
  defaultValue: "default",
  options: Object.fromEntries(
    Object.entries(EDITOR_LINE_HEIGHTS).map(([key, ratio]) => [
      key,
      `${ratio}`,
    ]),
  ) as Record<keyof typeof EDITOR_LINE_HEIGHTS, string>,
})

/**
 * The one hook editors (and the settings page) consume: current option keys
 * with setters, plus the resolved Monaco values.
 */
export function useEditorSettings() {
  const [palette, setPalette] = useEditorPalette()
  const [fontSizeKey, setFontSizeKey] = useEditorFontSize()
  const [lineHeightKey, setLineHeightKey] = useEditorLineHeight()

  const fontSize = EDITOR_FONT_SIZES[fontSizeKey]

  return {
    palette,
    setPalette,
    fontSizeKey,
    setFontSizeKey,
    lineHeightKey,
    setLineHeightKey,
    /** Monaco `theme` prop value for the chosen palette. */
    monacoTheme: EDITOR_PALETTES[palette],
    /** Monaco `fontSize` option, in px. */
    fontSize,
    /** Monaco `lineHeight` option, in px (ratio × font size). */
    lineHeight: Math.round(fontSize * EDITOR_LINE_HEIGHTS[lineHeightKey]),
  }
}
